import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Tooltip,
  Chip,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterAlt as FilterIcon,
  Edit as EditIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { userService } from '../services/userService';

// Esquema de validación para el formulario de usuario
const userSchema = Yup.object().shape({
  name: Yup.string().required('El nombre es obligatorio'),
  email: Yup.string().email('Email inválido').required('El email es obligatorio'),
  role: Yup.string().oneOf(['admin', 'user', 'superadmin'], 'Rol inválido').required('El rol es obligatorio'),
  password: Yup.string()
    .when('isNew', {
      is: true,
      then: () => Yup.string()
        .min(6, 'La contraseña debe tener al menos 6 caracteres')
        .required('La contraseña es obligatoria')
    })
});

// Roles disponibles
const roles = [
  { value: 'admin', label: 'Administrador' },
  { value: 'user', label: 'Usuario' }
];

function Users() {
  // Estados para datos
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  
  // Estados para feedback
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Estados para filtros
  const [filters, setFilters] = useState({
    search: '',
    role: '',
    active: ''
  });
  
  // Estado para paginación
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Estados para modales
  const [openUserModal, setOpenUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [openChangePasswordModal, setOpenChangePasswordModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Cargar usuarios al montar el componente y cuando cambien los filtros
  useEffect(() => {
    loadUsers();
  }, [page, rowsPerPage, filters]);

  // Función para cargar usuarios
  const loadUsers = async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1, // Backend usa páginas 1-indexed
        limit: rowsPerPage,
        search: filters.search,
        role: filters.role,
        active: filters.active
      };

      const result = await userService.getUsers(params);
      
      if (result.success) {
        // El backend devuelve { users, pagination }
        setUsers(result.data.users || []);
        setTotalCount(result.data.pagination?.total || 0);
      } else {
        showSnackbar('Error al cargar usuarios: ' + result.message, 'error');
      }
    } catch (error) {
      showSnackbar('Error al cargar usuarios', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Función para mostrar mensajes
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };
  
  // Manejadores para cambios en filtros
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: value
    });
    setPage(0); // Resetear a la primera página al filtrar
  };
  
  // Manejadores para paginación
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Manejadores para modales
  const handleOpenUserModal = (user = null) => {
    setSelectedUser(user);
    setOpenUserModal(true);
  };
  
  const handleCloseUserModal = () => {
    setOpenUserModal(false);
    setSelectedUser(null);
  };
  
  const handleOpenChangePasswordModal = (user) => {
    setSelectedUser(user);
    setOpenChangePasswordModal(true);
  };
  
  const handleCloseChangePasswordModal = () => {
    setOpenChangePasswordModal(false);
    setSelectedUser(null);
  };
  
  // Configurar formulario con Formik
  const formik = useFormik({
    initialValues: selectedUser ? {
      ...selectedUser,
      password: '',
      isNew: false
    } : {
      name: '',
      email: '',
      password: '',
      role: 'user',
      active: true,
      isNew: true
    },
    validationSchema: userSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        let result;
        
        if (values.isNew) {
          // Crear nuevo usuario
          const { isNew, ...userData } = values;
          result = await userService.createUser(userData);
        } else {
          // Actualizar usuario existente
          const { isNew, password, ...userData } = values;
          result = await userService.updateUser(selectedUser.id, userData);
        }
        
        if (result.success) {
          showSnackbar(
            values.isNew ? 'Usuario creado correctamente' : 'Usuario actualizado correctamente'
          );
      handleCloseUserModal();
          loadUsers(); // Recargar la lista
        } else {
          showSnackbar('Error: ' + result.message, 'error');
        }
      } catch (error) {
        showSnackbar('Error al guardar usuario', 'error');
      } finally {
        setIsSubmitting(false);
      }
    }
  });
  
  // Manejador para cambiar estado de usuario
  const handleToggleStatus = async (user) => {
    try {
      const result = await userService.toggleUserStatus(user.id);
      
      if (result.success) {
        showSnackbar(
          `Usuario ${user.active ? 'desactivado' : 'activado'} correctamente`
        );
        loadUsers(); // Recargar la lista
      } else {
        showSnackbar('Error: ' + result.message, 'error');
      }
    } catch (error) {
      showSnackbar('Error al cambiar estado del usuario', 'error');
    }
  };
  
  // Formulario para cambiar contraseña
  const passwordFormik = useFormik({
    initialValues: {
      newPassword: '',
      confirmPassword: ''
    },
    validationSchema: Yup.object({
      newPassword: Yup.string()
        .min(6, 'La contraseña debe tener al menos 6 caracteres')
        .required('La nueva contraseña es obligatoria'),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref('newPassword'), null], 'Las contraseñas deben coincidir')
        .required('Confirma la nueva contraseña')
    }),
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        const result = await userService.changeUserPassword(selectedUser.id, values.newPassword);
        
        if (result.success) {
          showSnackbar('Contraseña cambiada correctamente');
      handleCloseChangePasswordModal();
          passwordFormik.resetForm();
        } else {
          showSnackbar('Error: ' + result.message, 'error');
        }
      } catch (error) {
        showSnackbar('Error al cambiar contraseña', 'error');
      } finally {
        setIsSubmitting(false);
      }
    }
  });
  
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Gestión de Usuarios</Typography>
        <Button
          variant="contained"
          color="primary"
          size="large"
          startIcon={<AddIcon />}
          onClick={() => handleOpenUserModal()}
          sx={{ fontWeight: 'bold' }}
        >
          Nuevo Usuario
        </Button>
      </Box>
      
      {/* Sección de filtros */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          <FilterIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Filtros
        </Typography>
        
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              name="search"
              label="Buscar por nombre o email"
              value={filters.search}
              onChange={handleFilterChange}
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: 'action.active', mr: 1 }} />
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Rol</InputLabel>
              <Select
                name="role"
                value={filters.role}
                label="Rol"
                onChange={handleFilterChange}
              >
                <MenuItem value="">Todos</MenuItem>
                {roles.map(role => (
                  <MenuItem key={role.value} value={role.value}>
                    {role.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Estado</InputLabel>
              <Select
                name="active"
                value={filters.active}
                label="Estado"
                onChange={handleFilterChange}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="true">Activos</MenuItem>
                <MenuItem value="false">Inactivos</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Tabla de usuarios */}
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nombre</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Rol</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Último Acceso</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No hay usuarios disponibles
                </TableCell>
              </TableRow>
            ) : (
              users.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((user) => (
                <TableRow key={user.id} hover>
                  <TableCell component="th" scope="row">
                    {user.name}
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Chip 
                      label={roles.find(r => r.value === user.role)?.label || user.role} 
                      size="small" 
                      color={user.role === 'superadmin' ? 'secondary' : user.role === 'admin' ? 'primary' : 'default'} 
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={user.active ? 'Activo' : 'Inactivo'} 
                      size="small" 
                      color={user.active ? 'success' : 'error'} 
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    {user.lastLogin ? new Date(user.lastLogin).toLocaleString() : 'Nunca'}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Editar">
                      <IconButton 
                        size="small" 
                        color="secondary"
                        onClick={() => handleOpenUserModal(user)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Cambiar contraseña">
                      <IconButton 
                        size="small" 
                        color="info"
                        onClick={() => handleOpenChangePasswordModal(user)}
                      >
                        <LockIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={user.active ? 'Desactivar' : 'Activar'}>
                      <IconButton 
                        size="small" 
                        color={user.active ? 'error' : 'success'}
                        onClick={() => handleToggleStatus(user)}
                      >
                        {user.active ? <LockOpenIcon /> : <LockIcon />}
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        />
      </TableContainer>
      
      {/* Modal para crear/editar/ver usuario */}
      <Dialog
        open={openUserModal}
        onClose={handleCloseUserModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedUser ? 'Editar Usuario' : 'Nuevo Usuario'}
        </DialogTitle>
        
        <DialogContent>
          <Box component="form" onSubmit={formik.handleSubmit} noValidate sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  name="name"
                  label="Nombre"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  name="email"
                  label="Email"
                  type="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                />
              </Grid>
              
              {!selectedUser && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  name="password"
                    label="Contraseña"
                  type="password"
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  error={formik.touched.password && Boolean(formik.errors.password)}
                  helperText={formik.touched.password && formik.errors.password}
                />
              </Grid>
              )}
              
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Rol</InputLabel>
                  <Select
                    name="role"
                    value={formik.values.role}
                    label="Rol"
                    onChange={formik.handleChange}
                    error={formik.touched.role && Boolean(formik.errors.role)}
                  >
                    {roles.map(role => (
                      <MenuItem key={role.value} value={role.value}>
                        {role.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        name="active"
                        checked={formik.values.active}
                        onChange={formik.handleChange}
                      />
                    }
                    label="Usuario Activo"
                  />
                </Grid>
            </Grid>
          </Box>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleCloseUserModal}>
            Cancelar
          </Button>
          
            <Button 
              type="submit" 
              variant="contained" 
              onClick={formik.handleSubmit}
            disabled={isSubmitting}
            >
            {isSubmitting ? (
              <CircularProgress size={20} />
            ) : (
              selectedUser ? 'Guardar Cambios' : 'Crear Usuario'
            )}
            </Button>
        </DialogActions>
      </Dialog>
      
      {/* Modal para cambiar contraseña */}
      <Dialog
        open={openChangePasswordModal}
        onClose={handleCloseChangePasswordModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Cambiar Contraseña
        </DialogTitle>
        
        <DialogContent>
          <Box component="form" onSubmit={passwordFormik.handleSubmit} noValidate sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  name="newPassword"
                  label="Nueva Contraseña"
                  type="password"
                  value={passwordFormik.values.newPassword}
                  onChange={passwordFormik.handleChange}
                  error={passwordFormik.touched.newPassword && Boolean(passwordFormik.errors.newPassword)}
                  helperText={passwordFormik.touched.newPassword && passwordFormik.errors.newPassword}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  name="confirmPassword"
                  label="Confirmar Contraseña"
                  type="password"
                  value={passwordFormik.values.confirmPassword}
                  onChange={passwordFormik.handleChange}
                  error={passwordFormik.touched.confirmPassword && Boolean(passwordFormik.errors.confirmPassword)}
                  helperText={passwordFormik.touched.confirmPassword && passwordFormik.errors.confirmPassword}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleCloseChangePasswordModal}>
            Cancelar
          </Button>
          
          <Button 
            type="submit" 
            variant="contained" 
            onClick={passwordFormik.handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <CircularProgress size={20} />
            ) : (
              'Cambiar Contraseña'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para mensajes */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Users; 